import React, { useState } from 'react';
import { useAuditorStore } from '@/stores/auditorStore';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { apiClient } from '@/services/api';

export const BusinessSwitcher: React.FC = () => {
  const { currentBusiness, accessibleBusinesses, setCurrentBusiness } = useAuditorStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSwitch = async (business: typeof accessibleBusinesses[0]) => {
    if (business.merchant_id === currentBusiness?.merchant_id) {
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await apiClient.switchBusiness(business.merchant_id);

      if (data.success) {
        // Update the current business in the store
        setCurrentBusiness(business);
        setIsOpen(false);
        // Reload to fetch new business data
        window.location.reload();
      } else {
        console.error('Failed to switch business');
      }
    } catch (error) {
      console.error('Error switching business:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!accessibleBusinesses || accessibleBusinesses.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors min-w-[250px]"
        disabled={isLoading}
      >
        <Building2 className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <div className="flex-1 text-left">
          <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
            {currentBusiness?.name || 'Select Business'}
          </div>
          {currentBusiness?.gst_number && (
            <div className="text-xs text-gray-500 dark:text-gray-400">{currentBusiness.gst_number}</div>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                Accessible Businesses ({accessibleBusinesses.length})
              </div>
              {accessibleBusinesses.map((business) => (
                <button
                  key={business.id}
                  onClick={() => handleSwitch(business)}
                  disabled={isLoading}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    currentBusiness?.merchant_id === business.merchant_id ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                      {business.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {business.gst_number}
                    </div>
                    {business.city && business.state && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {business.city}, {business.state}
                      </div>
                    )}
                  </div>
                  {currentBusiness?.merchant_id === business.merchant_id && (
                    <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
